import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  Trophy,
  Users,
  Calendar,
  BarChart3,
  Shield,
  User,
  Menu,
  X,
  LogOut,
  Target,
  Home,
  Mail,
  Settings,
  MessageSquare,
  FileText,
  UserCheck,
  ClipboardList,
  Building2,
  Eye,
} from "lucide-react";
import { Button } from "./ui/button";
import { getSettings } from "../lib/api";

const navItems = [
  { to: "/dashboard", icon: Home, label: "Dashboard" },
  { to: "/dashboard/teams", icon: Shield, label: "Teams" },
  { to: "/dashboard/players", icon: Users, label: "Players" },
  { to: "/dashboard/matches", icon: Calendar, label: "Matches" },
  { to: "/dashboard/standings", icon: BarChart3, label: "Standings" },
  { to: "/dashboard/knockout", icon: Trophy, label: "Knockout" },
  { to: "/dashboard/player-stats", icon: User, label: "Player Stats" },
  { to: "/dashboard/team-stats", icon: Target, label: "Team Stats" },
  { to: "/dashboard/referees", icon: UserCheck, label: "Officials" },
  { to: "/dashboard/match-officials", icon: ClipboardList, label: "Match Officials" },
  { to: "/dashboard/sponsors", icon: Building2, label: "Sponsors" },
  { to: "/dashboard/blog-manage", icon: FileText, label: "Blog" },
  { to: "/dashboard/messages", icon: Mail, label: "Messages" },
  { to: "/dashboard/analytics", icon: Eye, label: "Analytics" },
  { to: "/dashboard/settings", icon: Settings, label: "Settings" },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getSettings().then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const tournamentName = settings?.name || "GOODWILLERS INDOOR TOURNAMENT";
  const tournamentLogo = settings?.logo;

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass border-b border-border">
        <div className="flex items-center justify-between px-4 h-16">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            {tournamentLogo ? (
              <img src={tournamentLogo} alt="Logo" className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <Trophy className="w-6 h-6 text-primary" />
            )}
            <span className="font-heading text-sm font-bold uppercase tracking-wider text-primary truncate max-w-[180px]">
              GOODWILLERS
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-72 bg-card border-r border-border transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-6 py-5 border-b border-border">
            <div className="flex items-center gap-3">
              {tournamentLogo ? (
                <img src={tournamentLogo} alt="Logo" className="w-12 h-12 rounded-xl object-cover border border-primary/30" />
              ) : (
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center border border-primary/30">
                  <Trophy className="w-6 h-6 text-primary" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-sm font-bold uppercase tracking-wider leading-tight">
                  GOODWILLERS
                </h1>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">
                  Indoor Tournament
                </p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden absolute top-5 right-4 p-2 hover:bg-secondary rounded-lg transition-colors"
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                    isActive
                      ? "bg-primary/10 text-primary border border-primary/30"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                end={item.to === "/"}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Contact Link */}
          <div className="px-4 py-3 border-t border-border">
            <a
              href="/contact"
              target="_blank"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              data-testid="contact-link"
            >
              <MessageSquare className="w-5 h-5" />
              <span className="font-medium">Contact Page</span>
            </a>
          </div>

          {/* Logout */}
          <div className="p-4 border-t border-border">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 md:p-8 lg:p-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
